import { memo, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import { Close as CloseIcon } from "components/icons";
import { handleAxiosError } from "app/services/app.error.handler";
import { parseApiError } from "app/services/api.error.parser";
import { loadAllClients } from "app/services/client.service";
import { useDispatch, useSelector } from "react-redux";
import MDBox from "components/MDBox";
import MasterDataSelect from "app/components/shared/MasterDataSelect";
import { clientActions } from "store/client-slice";
import { validateActivityTypeObject } from "utils/activityValidationUtil";
import { addActivityTypeToBackend, updateActivityTypeToBackend } from "app/services/admin.service";
import { trimOnBlur, trimTextFields } from "utils/trimOnBlur";

const obj = {
  id: null,
  activityDesc: "",
  order: 0,
  clientId: null,
};

const AddActivityTypeDialog = ({ open, setOpen, refreshActivitiesDataHandler, editObject }) => {
  const dispatch = useDispatch();
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("info"); // "error" or "warning" or "info" or "success"
  const [data, setData] = useState(obj);
  const clients = useSelector((state) => state.client.rows);

  useEffect(() => {
    if (clients.length !== 0) return;
    loadAllClients()
      .then((response) => dispatch(clientActions.replaceData(response.data)))
      .catch((error) => {
        dispatch(clientActions.replaceData([]));
        handleAxiosError(error);
      });
  }, [clients.length, dispatch]);

  useEffect(() => {
    setMessage("");
    setMessageOpen(false);
    setSeverity("info");
    if (!open) return;
    if (editObject) {
      setData({
        id: parseInt(editObject.id),
        activityDesc: editObject.activityDesc,
        order: editObject.order,
        clientId: editObject.client.id,
      });
    } else {
      setData(obj);
    }
  }, [open, editObject]);

  const showBackendError = (error, fallback) => {
    const parsed = parseApiError(error);
    if (parsed.isUnauthorized) {
      handleAxiosError(error);
      return;
    }
    setErrorAction(parsed.message || fallback);
  };

  const saveActivityTypeInBackend = async () => {
    const payload = trimTextFields(data);
    if (validateActivityTypeObject(payload) && !editObject) await addActivityTypeInvoker(payload);
    else if (validateActivityTypeObject(payload) && editObject)
      await updateActivityTypeInvoker(payload);
    else {
      setErrorAction(
        "One or more required fields for Activity Type are missing or having invalid value"
      );
    }
  };

  const addActivityTypeInvoker = async (clientObject) => {
    await addActivityTypeToBackend(clientObject)
      .then((data) => {
        setSuccessAction("Activity Type added successfully");
        setTimeout(() => {
          setData(obj);
          setMessageOpen(false);
          setMessage("");
          setOpen(false);
          refreshActivitiesDataHandler();
        }, 1000);
      })
      .catch((error) => {
        showBackendError(error, "Failed to add activity type. Please try again.");
      });
  };

  const updateActivityTypeInvoker = async (data) => {
    const editObjectId = data.id;
    await updateActivityTypeToBackend(editObjectId, data)
      .then((data) => {
        setSuccessAction("Activity Type Updated successfully");
        setTimeout(() => {
          setData(obj);
          setMessageOpen(false);
          setMessage("");
          setOpen(false);
          refreshActivitiesDataHandler();
        }, 1000);
      })
      .catch((error) => {
        showBackendError(error, "Failed to update activity type. Please try again.");
      });
  };

  const setErrorAction = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  const setSuccessAction = (message) => {
    setMessage(message);
    setMessageOpen(true);
    setSeverity("success");
  };

  return (
    <Dialog open={open} fullWidth={true} maxWidth="sm">
      <DialogTitle>{editObject ? `Update Activity Type` : `Add New Activity Type`}</DialogTitle>
      <DialogContent>
        <MDBox my={2}>
          <TextField
            id="activityDesc"
            label="Title"
            fullWidth
            variant="outlined"
            value={data?.activityDesc || ""}
            onBlur={trimOnBlur(setData, "activityDesc")}
            onChange={(e) => setData({ ...data, activityDesc: e.target.value })}
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="order"
            label="Order"
            fullWidth
            variant="outlined"
            value={data?.order ?? 0}
            onChange={(e) => setData({ ...data, order: e.target.value })}
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2}>
          <MasterDataSelect
            id="client"
            label="Client"
            placeholder="Select a client"
            options={clients}
            value={data?.clientId || ""}
            onChange={(e) => setData({ ...data, clientId: e.target.value })}
            helperText="*Required"
          />
        </MDBox>
        <Collapse in={messageOpen}>
          <Alert
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setMessageOpen(false);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            severity={severity}
            sx={{ mb: 2 }}
          >
            {message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
        <Button onClick={saveActivityTypeInBackend} variant="contained">
          {editObject ? `Update` : `Add`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddActivityTypeDialog);
